/* Minimal Web Component registration helper without the ShadowDOM
 *
 * Vue 3 can automatically generate and register Web Components from Vue components, but it adds a
 * good bit of build complexity, and opts us into the ShadowDOM.  This means each component would
 * have to negotiate how to opt-in to our global (e.g., bulma, etc) styles.
 *
 * This alternative minimal implementation acts similarly, but does not encapsulate the component
 * in a shadowRoot.  It registers an HTML tag, binds it to a component, and parses attributes to
 * pass along to the component.
 */

import { camelize, createApp, getCurrentInstance } from "vue";

/* Browsers have not standardized `parsedCallback`.  We need this to defer mounting the component
 * when the entire tag has been parsed (on </component-name>), instead of possibly being mounted
 * when the browser encounters the opening tag.  This allows the component to *replace* the
 * fall-back content within the element body.
 *
 * This small polyfill adds this callback.
 */
import HTMLParsedElement from "html-parsed-element";

export const findPropDefinition = (component, propName) => {
  const props = component.props;
  return props[propName] ?? props[camelize(propName)] ?? null;
};

/* In the DOM, attributes are all strings.  We can use Vue component prop type definitions to cast
 * "simple" types automatically.  Given a component, an attribute/prop name, and a value, we return
 * `value` casted to the type the component specifies for the prop, if possible.
 *
 * Vue components can allow a prop to accept multiple types.  In that case, we only consider the
 * first: Anything else would be ambiguous.
 *
 * Array and Object prop types are cast via a JSON parse.
 */

const castProp = (component, propName, value) => {
  const def = findPropDefinition(component, propName);

  if (!def) {
    return value;
  }

  const propType = def.type instanceof Array ? def.type[0] : def.type;

  switch (propType) {
    case String:
      return value;
    case Number:
      return +value;
    case Boolean:
      /* Note: This considers "undefined" true to handle Boolean value-less attributes:
       *  <some-element required> => required: true
       */
      return !["false", "0", "no"].includes(value?.toLowerCase());
    case Array:
    case Object:
      return JSON.parse(value);
    default:
      throw new Error(`Could not cast prop "${propName}" to type "${propType}"`);
  }
};

/* Examines `node` and returns a props object with values casted properly for component. */
const extractProps = (node, component) => {
  let result = {};
  for (const attr of node.attributes) {
    const name = attr.name.startsWith(":") ? attr.name.slice(1) : attr.name;
    result[name] = castProp(component, name, attr.value);
  }

  return result;
};

/* registers a Web Component, binding an HTML element tag name to a Vue component. */
export const registerTag = (name, { component, beforeMount }) => {
  const cls = class extends HTMLParsedElement {
    parsedCallback() {
      const wrappedComponent = {
        ...component,
        // Wrap the setup function to intercept emit calls
        setup(props, context) {
          const originalEmit = context.emit;
          const instance = getCurrentInstance();

          // Wrap emits, calling the original emit and dispatch a dom event for root level components
          const emit = (event, ...args) => {
            // Our overridden emit calls the normal emit
            originalEmit(event, ...args);

            // And then dispatches a dom event for root level components only
            if (instance && !instance.parent) {
              const el = instance.vnode.el;
              el?.dispatchEvent(
                new CustomEvent(event, {
                  detail: args,
                  bubbles: true,
                  composed: true,
                })
              );
            }
          };

          // Call the original setup function we're wrapping
          return component.setup?.(props, { ...context, emit });
        },
      };

      const app = createApp({ name, ...wrappedComponent }, extractProps(this, component));
      beforeMount?.(app);

      // Prevent double styling. Will be passed as prop to the component
      this.removeAttribute("class");
      this.removeAttribute("style");

      app.mount(this);
    }
  };

  customElements.define(name, cls);
};
